import { Stack, Typography, useTheme } from "@mui/material";
import {
	type Control,
	type FieldValues,
	type Path,
	useWatch,
} from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type InvestmentAdditionalFieldsProps<
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
	signedAtLabel?: string;
	disableKeyFields?: boolean;
};

const PageResources = Resources.Portfolio.AddInvestment;

export const InvestmentAdditionalFields = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>({
	control,
	signedAtLabel,
	disableKeyFields = false,
}: InvestmentAdditionalFieldsProps<FormType, TContext, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();
	const signedAt: Date | null = useWatch({
		control,
		name: "signedAt" as Path<FormType>,
	});

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name={"signedAt" as Path<FormType>}
					label={signedAtLabel ?? t(PageResources.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name={"startAt" as Path<FormType>}
					label={t(PageResources.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name={"endAt" as Path<FormType>}
					label={t(PageResources.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					disabled={disableKeyFields}
					minDate={signedAt ?? undefined}
					isLast
				/>
			</Stack>
		</>
	);
};
